import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  Tooltip,
  Stack,
  List,
  ListItem,
  ListItemText,
  ButtonGroup,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Edit,
  Calculate,
  Payment,
  Settings as SettingsIcon,
  Search,
  Clear,
  AttachMoney,
  Payments,
  TrendingUp,
  History,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, isWithinInterval } from 'date-fns';
import { DataTable, StatusChip } from '../components';
import api from '../services/api';

const Commissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Stats
  const [stats, setStats] = useState({
    totalPending: 0,
    totalFinalized: 0,
    paidThisMonth: 0,
    paidYTD: 0,
  });

  // Modals
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'check',
    payment_reference: '',
    notes: '',
  });

  // Chart data
  const [trendData, setTrendData] = useState([]);

  useEffect(() => {
    fetchCommissions();
    fetchSales();
    fetchUsers();
    fetchCommissionTiers();
    fetchStats();
  }, [statusFilter, staffFilter, startDate, endDate]);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const params = {};

      if (statusFilter !== 'all') params.status = statusFilter;
      if (staffFilter !== 'all') params.staff_id = staffFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/commissions', { params });
      const commissionsData = response.data?.commissions || response.data || [];
      setCommissions(commissionsData);
      calculateTrendData(commissionsData);
    } catch (error) {
      console.error('Error fetching commissions:', error);
      // Use mock data if API fails
      setCommissions(generateMockCommissions());
      showSnackbar('Using demo data - API not available', 'info');
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      const response = await api.get('/sales');
      setSales(response.data?.sales || response.data || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchCommissionTiers = async () => {
    try {
      const response = await api.get('/commissions/tiers');
      setCommissionTiers(response.data?.tiers || response.data || generateMockTiers());
    } catch (error) {
      console.error('Error fetching commission tiers:', error);
      setCommissionTiers(generateMockTiers());
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/commissions');
      const allCommissions = response.data?.commissions || response.data || generateMockCommissions();

      const now = new Date();
      const firstDayOfMonth = startOfMonth(now);
      const firstDayOfYear = new Date(now.getFullYear(), 0, 1);

      const totalPending = allCommissions
        .filter(c => c.status === 'pending' || c.status === 'calculated')
        .reduce((sum, c) => sum + (c.commission_amount || 0), 0);

      const totalFinalized = allCommissions
        .filter(c => c.status === 'finalized')
        .reduce((sum, c) => sum + (c.commission_amount || 0), 0);

      const paidThisMonth = allCommissions
        .filter(c => {
          if (c.status !== 'paid') return false;
          const paidDate = c.payment_date ? new Date(c.payment_date) : null;
          return paidDate && paidDate >= firstDayOfMonth;
        })
        .reduce((sum, c) => sum + (c.commission_amount || 0), 0);

      const paidYTD = allCommissions
        .filter(c => {
          if (c.status !== 'paid') return false;
          const paidDate = c.payment_date ? new Date(c.payment_date) : null;
          return paidDate && paidDate >= firstDayOfYear;
        })
        .reduce((sum, c) => sum + (c.commission_amount || 0), 0);

      setStats({
        totalPending,
        totalFinalized,
        paidThisMonth,
        paidYTD,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Calculate stats from mock data
      const mockCommissions = generateMockCommissions();
      const totalPending = mockCommissions
        .filter(c => c.status === 'pending' || c.status === 'calculated')
        .reduce((sum, c) => sum + c.commission_amount, 0);
      const totalFinalized = mockCommissions
        .filter(c => c.status === 'finalized')
        .reduce((sum, c) => sum + c.commission_amount, 0);
      const paidThisMonth = mockCommissions
        .filter(c => c.status === 'paid')
        .reduce((sum, c) => sum + c.commission_amount, 0) * 0.3;
      const paidYTD = mockCommissions
        .filter(c => c.status === 'paid')
        .reduce((sum, c) => sum + c.commission_amount, 0);

      setStats({ totalPending, totalFinalized, paidThisMonth, paidYTD });
    }
  };

  const calculateTrendData = (commissionsData) => {
    const monthsData = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const monthCommissions = commissionsData.filter((c) => {
        if (!c.payment_date) return false;
        const commissionDate = parseISO(c.payment_date);
        return isWithinInterval(commissionDate, { start: monthStart, end: monthEnd }) && c.status === 'paid';
      });

      const amount = monthCommissions.reduce((sum, c) => sum + (c.commission_amount || 0), 0);

      monthsData.push({
        month: format(date, 'MMM yyyy'),
        amount,
      });
    }
    setTrendData(monthsData);
  };

  const handleCalculatePreview = async (saleId) => {
    try {
      const response = await api.post(`/commissions/preview/${saleId}`);
      setPreviewData(response.data);
      setPreviewModalOpen(true);
    } catch (error) {
      console.error('Error calculating preview:', error);
      // Generate mock preview
      const sale = sales.find(s => s.id === saleId);
      const mockPreview = {
        sale_name: sale?.name || 'Estate Sale',
        sale_total: sale?.total_revenue || 15000,
        commission_rate: 0.10,
        commission_amount: (sale?.total_revenue || 15000) * 0.10,
        staff_member: 'John Doe',
        tier: 'Standard (10%)',
      };
      setPreviewData(mockPreview);
      setPreviewModalOpen(true);
    }
  };

  const handleFinalizeCommission = async (commissionId) => {
    try {
      await api.put(`/commissions/${commissionId}`, { status: 'finalized' });
      showSnackbar('Commission finalized successfully', 'success');
      fetchCommissions();
      fetchStats();
      if (selectedCommission?.id === commissionId) {
        const response = await api.get(`/commissions/${commissionId}`);
        setSelectedCommission(response.data);
      }
    } catch (error) {
      console.error('Error finalizing commission:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to finalize commission', 'error');
    }
  };

  const handleMarkPaid = async (commission) => {
    setSelectedCommission(commission);
    setPaymentData({
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      payment_method: 'check',
      payment_reference: '',
      notes: '',
    });
    setPaymentModalOpen(true);
  };

  const handleSavePayment = async () => {
    try {
      await api.put(`/commissions/${selectedCommission.id}`, {
        status: 'paid',
        payment_date: paymentData.payment_date,
        payment_method: paymentData.payment_method,
        payment_reference: paymentData.payment_reference,
        payment_notes: paymentData.notes,
      });
      showSnackbar('Commission marked as paid', 'success');
      setPaymentModalOpen(false);
      fetchCommissions();
      fetchStats();
      if (detailModalOpen) {
        const response = await api.get(`/commissions/${selectedCommission.id}`);
        setSelectedCommission(response.data);
      }
    } catch (error) {
      console.error('Error marking commission as paid:', error);
      showSnackbar('Failed to mark commission as paid', 'error');
    }
  };

  const handleViewDetails = async (commission) => {
    try {
      const response = await api.get(`/commissions/${commission.id}`);
      setSelectedCommission(response.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Error fetching commission details:', error);
      // Use provided commission data as fallback
      setSelectedCommission({
        ...commission,
        status_history: [
          { status: 'pending', date: commission.created_at, notes: 'Commission created' },
          ...(commission.status !== 'pending' ? [{ status: commission.status, date: commission.updated_at }] : []),
        ],
      });
      setDetailModalOpen(true);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChipProps = (status) => {
    const statusMap = {
      pending: 'default',
      calculated: 'info',
      finalized: 'warning',
      paid: 'success',
    };
    return statusMap[status] || 'default';
  };

  const filteredCommissions = useMemo(() => {
    return commissions.filter(commission => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        commission.sale_name?.toLowerCase().includes(searchLower) ||
        commission.staff_name?.toLowerCase().includes(searchLower);

      return matchesSearch;
    });
  }, [commissions, searchQuery]);

  const columns = [
    {
      field: 'sale_name',
      headerName: 'Sale Name',
      sortable: true,
      searchable: true,
      minWidth: 200,
      render: (row) => row.sale_name || 'N/A',
    },
    {
      field: 'staff_name',
      headerName: 'Staff Member',
      sortable: true,
      searchable: true,
      minWidth: 150,
      render: (row) => row.staff_name || 'Unassigned',
    },
    {
      field: 'sale_total',
      headerName: 'Sale Total',
      sortable: true,
      align: 'right',
      minWidth: 120,
      render: (row) => `$${Number(row.sale_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: 'commission_rate',
      headerName: 'Rate',
      sortable: true,
      align: 'center',
      minWidth: 80,
      render: (row) => `${((row.commission_rate || 0) * 100).toFixed(1)}%`,
    },
    {
      field: 'commission_amount',
      headerName: 'Commission',
      sortable: true,
      align: 'right',
      minWidth: 120,
      render: (row) => (
        <Typography variant="body2" fontWeight="bold" color="success.main">
          ${Number(row.commission_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      minWidth: 120,
      render: (row) => <StatusChip status={row.status} />,
    },
    {
      field: 'payment_date',
      headerName: 'Payment Date',
      sortable: true,
      minWidth: 120,
      render: (row) => row.payment_date ? new Date(row.payment_date).toLocaleDateString() : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'center',
      minWidth: 150,
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status === 'calculated' && (
            <Tooltip title="Finalize">
              <IconButton size="small" onClick={() => handleFinalizeCommission(row.id)} color="primary">
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {row.status === 'finalized' && (
            <Tooltip title="Mark as Paid">
              <IconButton size="small" onClick={() => handleMarkPaid(row)} color="success">
                <Payment fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Commissions Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage staff commissions
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => window.location.href = '/settings'}
        >
          Manage Tiers
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Pending
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    ${stats.totalPending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Awaiting completion
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'warning.lighter',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <AttachMoney sx={{ color: 'warning.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Finalized
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    ${stats.totalFinalized.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ready for payment
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'info.lighter',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Payments sx={{ color: 'info.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Paid This Month
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    ${stats.paidThisMonth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(), 'MMMM yyyy')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'success.lighter',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <CheckCircle sx={{ color: 'success.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Paid YTD
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    ${stats.paidYTD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Year to date {new Date().getFullYear()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'primary.lighter',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <TrendingUp sx={{ color: 'primary.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Commission Trend Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Commission Payments Trend (Last 6 Months)
          </Typography>
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#003366"
                  strokeWidth={3}
                  dot={{ fill: '#D4AF37', r: 5 }}
                  name="Commission Paid"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Commission Tiers */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Commission Tier Structure</Typography>
            <Button
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => window.location.href = '/settings'}
            >
              Edit Tiers
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tier Name</TableCell>
                  <TableCell align="right">Minimum Sale Amount</TableCell>
                  <TableCell align="center">Commission Rate</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commissionTiers.map((tier, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {tier.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      ${tier.threshold.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${(tier.rate * 100).toFixed(1)}%`}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tier.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by sale or staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="calculated">Calculated</MenuItem>
                  <MenuItem value="finalized">Finalized</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Staff Member</InputLabel>
                <Select
                  value={staffFilter}
                  label="Staff Member"
                  onChange={(e) => setStaffFilter(e.target.value)}
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Commissions Table */}
      <DataTable
        columns={columns}
        data={filteredCommissions}
        loading={loading}
        searchable={false}
        emptyMessage="No commissions found"
        defaultRowsPerPage={25}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Commission Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedCommission && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Commission Details</Typography>
                <StatusChip status={selectedCommission.status} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Sale Information */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Sale Information
              </Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Sale Name</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedCommission.sale_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Staff Member</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedCommission.staff_name || 'Unassigned'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Sale Date</Typography>
                  <Typography variant="body1">
                    {selectedCommission.sale_date ? new Date(selectedCommission.sale_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Sale Status</Typography>
                  <Typography variant="body1">
                    {selectedCommission.sale_status || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Commission Breakdown */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Commission Breakdown
              </Typography>
              <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Sale Total</Typography>
                    <Typography variant="h6">
                      ${Number(selectedCommission.sale_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Commission Rate</Typography>
                    <Typography variant="h6">
                      {((selectedCommission.commission_rate || 0) * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Commission Amount</Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      ${Number(selectedCommission.commission_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Information */}
              {selectedCommission.status === 'paid' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Payment Information
                  </Typography>
                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                      <Typography variant="body1">
                        {selectedCommission.payment_date ? new Date(selectedCommission.payment_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                      <Typography variant="body1">
                        {selectedCommission.payment_method || 'N/A'}
                      </Typography>
                    </Grid>
                    {selectedCommission.payment_reference && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Payment Reference</Typography>
                        <Typography variant="body1">{selectedCommission.payment_reference}</Typography>
                      </Grid>
                    )}
                    {selectedCommission.payment_notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography variant="body1">{selectedCommission.payment_notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* Status History */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Status History
              </Typography>
              <List>
                {(selectedCommission.status_history || []).map((item, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <StatusChip status={item.status} />
                          <Typography variant="body2">
                            {item.date ? format(parseISO(item.date), 'MMM dd, yyyy hh:mm a') : 'N/A'}
                          </Typography>
                        </Box>
                      }
                      secondary={item.notes}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
              {selectedCommission.status === 'calculated' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                  onClick={() => handleFinalizeCommission(selectedCommission.id)}
                >
                  Finalize
                </Button>
              )}
              {selectedCommission.status === 'finalized' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Payment />}
                  onClick={() => {
                    setPaymentModalOpen(true);
                  }}
                >
                  Mark as Paid
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Preview Modal */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Commission Preview</DialogTitle>
        <DialogContent dividers>
          {previewData && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Sale</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {previewData.sale_name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Staff Member</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {previewData.staff_member}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Sale Total</Typography>
                  <Typography variant="h6">
                    ${previewData.sale_total.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Tier</Typography>
                  <Typography variant="body1">
                    {previewData.tier}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Estimated Commission</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    ${previewData.commission_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Modal */}
      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Commission Payment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Payment Date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentData.payment_method}
                  label="Payment Method"
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                >
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="direct_deposit">Direct Deposit</MenuItem>
                  <MenuItem value="wire_transfer">Wire Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Reference (Check #, Transaction ID, etc.)"
                value={paymentData.payment_reference}
                onChange={(e) => setPaymentData({ ...paymentData, payment_reference: e.target.value })}
                placeholder="e.g., Check #12345"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="Additional payment notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Payment />}
            onClick={handleSavePayment}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Mock data generators
const generateMockCommissions = () => [
  {
    id: 1,
    sale_name: 'Johnson Estate Sale',
    sale_total: 45000,
    commission_rate: 0.12,
    commission_amount: 5400,
    staff_name: 'John Doe',
    staff_id: 1,
    status: 'paid',
    payment_date: '2024-11-15',
    payment_method: 'Direct Deposit',
    payment_reference: 'DD-2024-1115',
    sale_date: '2024-11-10',
    sale_status: 'completed',
    created_at: '2024-11-10T10:00:00',
    updated_at: '2024-11-15T14:30:00',
  },
  {
    id: 2,
    sale_name: 'Smith Family Estate',
    sale_total: 28000,
    commission_rate: 0.10,
    commission_amount: 2800,
    staff_name: 'Jane Smith',
    staff_id: 2,
    status: 'finalized',
    payment_date: null,
    sale_date: '2024-11-20',
    sale_status: 'completed',
    created_at: '2024-11-20T09:00:00',
    updated_at: '2024-11-21T11:00:00',
  },
  {
    id: 3,
    sale_name: 'Williams Estate',
    sale_total: 62000,
    commission_rate: 0.15,
    commission_amount: 9300,
    staff_name: 'John Doe',
    staff_id: 1,
    status: 'calculated',
    payment_date: null,
    sale_date: '2024-11-25',
    sale_status: 'completed',
    created_at: '2024-11-25T10:00:00',
    updated_at: '2024-11-25T16:00:00',
  },
  {
    id: 4,
    sale_name: 'Brown Estate Sale',
    sale_total: 18000,
    commission_rate: 0.08,
    commission_amount: 1440,
    staff_name: 'Mike Johnson',
    staff_id: 3,
    status: 'pending',
    payment_date: null,
    sale_date: null,
    sale_status: 'active',
    created_at: '2024-11-28T08:00:00',
    updated_at: '2024-11-28T08:00:00',
  },
  {
    id: 5,
    sale_name: 'Davis Luxury Estate',
    sale_total: 95000,
    commission_rate: 0.15,
    commission_amount: 14250,
    staff_name: 'Jane Smith',
    staff_id: 2,
    status: 'paid',
    payment_date: '2024-10-30',
    payment_method: 'Check',
    payment_reference: 'CHK-10245',
    sale_date: '2024-10-25',
    sale_status: 'completed',
    created_at: '2024-10-25T10:00:00',
    updated_at: '2024-10-30T15:00:00',
  },
];

const generateMockTiers = () => [
  {
    name: 'Basic',
    threshold: 0,
    rate: 0.08,
    description: 'Sales up to $20,000',
  },
  {
    name: 'Standard',
    threshold: 20000,
    rate: 0.10,
    description: 'Sales from $20,000 to $50,000',
  },
  {
    name: 'Premium',
    threshold: 50000,
    rate: 0.12,
    description: 'Sales from $50,000 to $100,000',
  },
  {
    name: 'Elite',
    threshold: 100000,
    rate: 0.15,
    description: 'Sales over $100,000',
  },
];

export default Commissions;
